// src/pages/InvoicePayments.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import toast from "react-hot-toast";
import InvoicePrintPreviewModal from "../components/InvoicePrintPreviewModal";
import {
  FaMoneyBillWave,
  FaFilter,
  FaRedoAlt,
  FaRupeeSign,
  FaList,
  FaTimesCircle,
} from "react-icons/fa";
import { FiChevronDown, FiChevronUp, FiPrinter } from "react-icons/fi";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import { useGetInvoicesQuery } from "../toolkitServices/invoiceApi";
import {
  useAddCustomerReceiptMutation,
  useGetCustomerTransactionsQuery, // ✅ invoice related transactions list
} from "../toolkitServices/customerTransactionApi";
import { useGetSalesReturnsQuery } from "../toolkitServices/salesReturnApi";

import { handleDropDowns } from "../config/dropDownHelpers";
import CreateSalesReturnModal from "../components/salesReturn/CreateSalesReturnModal";
import ConfirmDialog from "../components/ConfirmDialog";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* ---------------- animations ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.45, ease: "easeOut" },
  },
});

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.35, ease: "easeOut" },
  }),
};

/* ------------ Tiny spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ---------------- small UI components ---------------- */
const ListLoadingPill = ({ show }) => (
  <AnimatePresence>
    {show ? (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm"
      >
        <span className="relative inline-flex h-4 w-4 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-300/60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
        </span>
        Loading…
      </motion.div>
    ) : null}
  </AnimatePresence>
);

const SkeletonTxRow = () => (
  <div className="rounded-2xl border bg-white px-3 py-2.5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-slate-200 animate-pulse" />
          <div className="h-4 w-44 rounded bg-slate-200 animate-pulse" />
        </div>
        <div className="mt-2 space-y-2">
          <div className="h-3 w-64 rounded bg-slate-200 animate-pulse" />
          <div className="h-3 w-40 rounded bg-slate-200 animate-pulse" />
        </div>
      </div>
      <div className="shrink-0 text-right space-y-2">
        <div className="h-5 w-24 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-3 w-14 rounded bg-slate-200 animate-pulse ml-auto" />
        <div className="h-7 w-16 rounded-full bg-slate-200 animate-pulse ml-auto" />
      </div>
    </div>
  </div>
);

const ListShimmerOverlay = ({ show }) => (
  <AnimatePresence>
    {show ? (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/45 to-white/70" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-r from-transparent via-slate-100/90 to-transparent animate-[shimmer_1.25s_linear_infinite]" />
        <style>{`
          @keyframes shimmer { 
            0% { transform: translateX(-100%); } 
            100% { transform: translateX(100%); } 
          }
        `}</style>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="rounded-2xl border bg-slate-50 p-5 text-center">
      <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border shadow-sm text-slate-700">
        {icon}
      </div>
      <div className="text-sm font-extrabold text-slate-800">{title}</div>
      <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>
    </div>
  );
}

function SegTabs({ value, onChange, tabs }) {
  return (
    <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200">
      {tabs.map((t) => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-[13px] transition ${
              active
                ? "bg-white shadow-sm border border-slate-200 text-slate-900"
                : "text-slate-600 hover:text-slate-900"
            }`}
            title={t.label}
            aria-label={t.label}
          >
            {t.icon || t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------ Helpers ------------ */
const up = (x) => String(x || "").toUpperCase();

const formatAmount = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normId = (x) => {
  if (!x) return "";
  if (typeof x === "string") return x;
  return x._id || x.id || "";
};

/* ------------ Invoice amount helpers ------------ */
const getInvoiceOutstanding = (inv) => {
  if (!inv) return 0;
  if (inv.totals && inv.totals.outstanding != null) {
    return Number(inv.totals.outstanding) || 0;
  }
  return (
    Number(
      inv.outstanding ?? inv.outstandingAmount ?? inv.balanceAmount ?? 0,
    ) || 0
  );
};

const getInvoiceTotal = (inv) => {
  if (!inv) return 0;
  return (
    Number(
      inv.totals?.grandTotal ??
        inv.invoiceAmount ??
        inv.totalAmount ??
        inv.grandTotal ??
        0,
    ) || 0
  );
};

const getInvoicePaid = (inv) => {
  if (!inv) return 0;
  return (
    Number(inv.totals?.paid ?? inv.paidAmount ?? inv.invoicePaid ?? 0) || 0
  );
};

/* ------------ UI atoms ------------ */
const MoneyPill = ({ value, tone = "default", label = "" }) => {
  const num = Number(value) || 0;

  const toneCls =
    tone === "good"
      ? "bg-emerald-50 ring-emerald-200 text-emerald-800"
      : tone === "bad"
        ? "bg-rose-50 ring-rose-200 text-rose-800"
        : "bg-slate-50 ring-slate-200 text-slate-800";

  return (
    <span
      className={`inline-flex items-center justify-end gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold tabular-nums tracking-tight ring-1 ${toneCls}`}
      title={`₹ ${formatAmount(num)}`}
    >
      {label ? <span className="mr-0.5 text-[9px] font-medium uppercase tracking-wide opacity-60">{label}</span> : null}
      <span className="text-[10px] opacity-70">₹</span>
      {formatAmount(num)}
    </span>
  );
};


/* ------------ Invoice transaction helpers ------------ */
const isReturnTxn = (r) => {
  const t = up(r?.sourceType || r?.type || r?.kind);
  return t === "SALES_RETURN" || t === "INVOICE_RETURN" || t === "REFUND";
};

const pickTxnAmount = (r) => {
  const credit = Number(r?.credit || r?.invoicePaid || 0) || 0;
  const debit = Number(r?.debit || 0) || 0;
  if (credit > 0) return credit;
  if (debit > 0) return debit;

  const tokens = String(r?.note || "").split(" ");
  const lastToken = tokens?.[tokens.length - 1] || "";
  const fallbackFromNote = String(lastToken).replace(/[^\d.]/g, "");
  const fallbackNum = fallbackFromNote ? Number(fallbackFromNote) : 0;
  return Number.isFinite(fallbackNum) ? fallbackNum : 0;
};

const pickTxnTone = (r) => {
  if (isReturnTxn(r)) return "bad";

  const credit = Number(r?.credit || r?.invoicePaid || 0) || 0;
  const debit = Number(r?.debit || 0) || 0;
  if (credit > 0) return "good";
  if (debit > 0) return "bad";
  return "default";
};

const txnTypeLabel = (r) => {
  const t = up(r?.sourceType || r?.type || r?.kind);
  switch (t) {
    case "PAYMENT":
      return "Receipt";
    case "INVOICE":
      return "Invoice";
    case "SALES_RETURN":
    case "INVOICE_RETURN":
      return "Invoice Return";
    case "REFUND":
      return "Refund";
    case "OPENING":
      return "Opening";
    case "URD":
      return "URD";
    case "SCHEME":
      return "Scheme";
    case "WALLET":
      return "Wallet";
    default:
      return t || "-";
  }
};

/* ====================================================================== */
/*                       MODAL: RECEIVE PAYMENT (INVOICE)                 */
/* ====================================================================== */
const InvoiceReceivePaymentModal = ({
  open,
  onClose,
  invoice,
  customerLabel,
  onSubmit,
  loading,
}) => {
  const [mode, setMode] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [refNo, setRefNo] = useState("");
  const [note, setNote] = useState("");

  const amountRef = useRef(null);

  const invTotal = getInvoiceTotal(invoice);
  const invPaid = getInvoicePaid(invoice);
  const invOutstanding = getInvoiceOutstanding(invoice);

  useEffect(() => {
    if (!open) return;
    setMode("Cash");
    setRefNo("");
    setNote("");
    const def = Number(invOutstanding || 0);
    setAmount(def > 0 ? String(Number(def.toFixed(2))) : "");
  }, [open, invOutstanding]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (amountRef.current) {
        amountRef.current.focus();
        amountRef.current.select?.();
      }
    }, 60);
    return () => clearTimeout(t);
  }, [open]);

  const amountNum = Number(amount) || 0;
  const max = Number(invOutstanding) || 0;
  const invalid = amountNum <= 0 || amountNum > max + 0.009;

  const handleAmountChange = (e) => {
    let raw = e.target.value;
    if (raw === "") return setAmount("");
    let num = parseFloat(raw);
    if (!Number.isFinite(num) || num < 0) num = 0;
    if (num > max) num = max;
    setAmount(String(num));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (invalid) return;

    const invoiceId = invoice?.invoiceId || invoice?._id || invoice?.id;
    const invoiceNo =
      invoice?.sourceNo ||
      invoice?.invNo ||
      invoice?.invoiceNo ||
      invoice?.invCode ||
      "-";

    onSubmit?.({
      kind: "INVOICE",
      mode,
      amount: Number(amountNum.toFixed(2)),
      refNo,
      note,
      allocations: [
        { invoiceId, invoiceNo, amount: Number(amountNum.toFixed(2)) },
      ],
    });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-2xl sm:rounded-2xl rounded-xl bg-white shadow-2xl border border-slate-100 p-3 sm:p-4 flex flex-col gap-3 max-h-[92vh] mx-2"
          initial={{ y: 28, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 28, opacity: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 240, damping: 22 }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-100">
                <FaMoneyBillWave className="text-base" />
              </div>
              <div className="flex flex-col min-w-0">
                <h2 className="text-sm font-extrabold text-slate-900 truncate">
                  Receive Payment
                </h2>
                <p className="text-[11px] text-slate-500 truncate">
                  {customerLabel || "-"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              disabled={loading}
            >
              Close
            </button>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3 text-[11px]">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="text-slate-700">
                <span className="font-extrabold text-slate-900">
                  {invoice?.sourceNo ||
                    invoice?.invNo ||
                    invoice?.invoiceNo ||
                    invoice?.invCode ||
                    "-"}
                </span>
                <span className="text-slate-400 ml-2">
                  {formatDateTime(
                    invoice?.invDate || invoice?.date || invoice?.createdAt,
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-slate-600">
                  Total:{" "}
                  <span className="font-semibold">
                    ₹ {formatAmount(invTotal)}
                  </span>
                </span>
                <span className="text-slate-600">
                  Paid:{" "}
                  <span className="font-semibold text-emerald-700">
                    ₹ {formatAmount(invPaid)}
                  </span>
                </span>
                <span className="text-slate-600">
                  Due:{" "}
                  <span className="font-semibold text-rose-600">
                    ₹ {formatAmount(invOutstanding)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Amount
                </label>
                <div className="flex items-center gap-1.5 rounded-2xl border border-slate-200 px-2.5 py-2 bg-white">
                  <FaRupeeSign className="text-[10px] text-slate-400" />
                  <input
                    ref={amountRef}
                    type="number"
                    step="0.01"
                    className="w-full border-none outline-none text-[12px] text-slate-900 placeholder-slate-400"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                  />
                </div>
                {amountNum > max + 0.009 ? (
                  <div className="mt-1 text-[10px] text-rose-600">
                    Max: ₹ {formatAmount(max)}
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Mode
                </label>
                <select
                  className="w-full rounded-2xl border border-slate-200 px-2.5 py-2 text-[12px] bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <option value="Cash">Cash</option>
                  {/* <option value="Card">Card</option> */}
                  <option value="UPI">UPI</option>
                  <option value="Bank">Bank</option>
                  {/* <option value="RTGS">RTGS</option> */}
                  <option value="Wallet">Wallet</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Ref / Cheque No.
                </label>
                <input
                  type="text"
                  className="w-full rounded-2xl border border-slate-200 px-2.5 py-2 text-[12px] bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={refNo}
                  onChange={(e) => setRefNo(e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Note
                </label>
                <input
                  type="text"
                  className="w-full rounded-2xl border border-slate-200 px-2.5 py-2 text-[12px] bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-[11px] gap-2">
              <div className="text-slate-500">
                {invalid ? (
                  <span className="text-rose-600">
                    Enter valid amount (1 to ₹ {formatAmount(max)}).
                  </span>
                ) : (
                  <span>Save entry and print receipt if needed.</span>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-[11px]"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading || invalid}
                  className="px-4 py-1.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-2 shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                >
                  <FaMoneyBillWave className="text-xs" />
                  {loading ? "Saving..." : "Confirm"}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ====================================================================== */
/*                              MAIN SCREEN                               */
/* ====================================================================== */
const InvoicePayments = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [customer, setCustomer] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  // Show the complete invoice list by default. Selecting a customer scopes
  // the same list through invoiceArgs.customerId below.
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);

  // ✅ right panel invoice txns search
  const [txSearch, setTxSearch] = useState("");

  // ✅ mobile view tabs (Invoices vs Transactions)
  const [mobileView, setMobileView] = useState("INVOICES");

  // modals
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);

  const [salesReturnOpen, setSalesReturnOpen] = useState(false);
  const [salesReturnInvoice, setSalesReturnInvoice] = useState(null);

  const [printConfirm, setPrintConfirm] = useState({
    open: false,
    row: null,
  });
  const [receiptPreview, setReceiptPreview] = useState({
  open: false,
  html: "",
  loading: false,
});

/* ------------ Receipt print helper ------------ */
const handleReceiptPrint = async (rowOrId) => {
  try {
    const row =
      typeof rowOrId === "string"
        ? { _id: rowOrId }
        : rowOrId || {};

    const ledgerId =
      row.ledgerId ||
      row.transactionId ||
      row.receiptId ||
      row._id ||
      row.id;

    if (!ledgerId) return;

    setReceiptPreview({
      open: true,
      html: "",
      loading: true,
    });

    const response = await fetch(
  `${API_BASE_URL}/customers/transactions/${ledgerId}/receipt-print`,
  {
    method: "GET",
    credentials: "include",
  }
);

    const html = await response.text();

    setReceiptPreview({
      open: true,
      html,
      loading: false,
    });
  } catch (err) {
    toast.error("Failed to load receipt");
  }
};

  /* Customers */
  const { data: customerRes, isLoading: customersLoading } =
    useGetAllCustomersQuery({ search: "", page: 1, limit: 500 });

  const rawCustomers = useMemo(
    () => customerRes?.rows || customerRes?.data || [],
    [customerRes],
  );

  const customerOptions = useMemo(
    () =>
      handleDropDowns({
        key: "customer",
        customers: rawCustomers,
      }) || [],
    [rawCustomers],
  );

  /* Invoices */
  const invoiceArgs = {
    page,
    limit,
    customerId: customer?.value || "",
    from: from || "",
    to: to || "",
    q: q || "",
  };

  const {
    data: invRes,
    isLoading: invLoading,
    isFetching: invFetching,
    refetch: refetchInvoices,
  } = useGetInvoicesQuery(invoiceArgs);

  const invoiceRows = useMemo(
    () => invRes?.rows || invRes?.data || [],
    [invRes?.rows, invRes?.data],
  );
  const total = invRes?.total || invRes?.count || invoiceRows.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  /* Sales Returns */
  const srHistoryArgs = useMemo(
    () => ({
      page: 1,
      limit: 5000,
      customerId: customer?.value || "",
    }),
    [customer?.value],
  );

  const { data: srHistoryRes } = useGetSalesReturnsQuery(srHistoryArgs);

  const srHistoryRows = useMemo(() => {
    const res = srHistoryRes;
    return res?.data || res?.rows || res?.items || res?.result || [];
  }, [srHistoryRes]);

  const srAgg = useMemo(() => {
    const amountByInvoiceId = {};
    const qtyByInvoiceIdByKey = {};

    for (const sr of srHistoryRows || []) {
      const invId = normId(sr?.invoiceId) || normId(sr?.invoiceId?._id);
      if (!invId) continue;

      const retAmt =
        Number(sr?.totals?.grandTotal ?? sr?.totals?.lineGrand ?? 0) || 0;
      amountByInvoiceId[invId] =
        (Number(amountByInvoiceId[invId]) || 0) + retAmt;

      const entries = sr?.stockEntries || [];
      if (!qtyByInvoiceIdByKey[invId]) qtyByInvoiceIdByKey[invId] = {};

      for (const e of entries) {
        const key =
          e?.labelledItemId ||
          e?.invoiceItemCode ||
          e?.itemCode ||
          `${e?.productId || ""}-${e?.designId || ""}-${e?.purityId || ""}`;

        const qty = Number(e?.qty || 0) || 0;
        qtyByInvoiceIdByKey[invId][key] =
          (Number(qtyByInvoiceIdByKey[invId][key]) || 0) + qty;
      }
    }

    return { amountByInvoiceId, qtyByInvoiceIdByKey };
  }, [srHistoryRows]);

  const rows = useMemo(() => {
    const list = (invoiceRows || []).map((inv) => {
      const invId = normId(inv?._id || inv?.invoiceId || inv);
      const totalAmt = getInvoiceTotal(inv);
      const paid = getInvoicePaid(inv);
      const due = getInvoiceOutstanding(inv);

      const returnedAmt = Number(srAgg.amountByInvoiceId?.[invId] || 0) || 0;
      const fullyReturned = totalAmt > 0 && returnedAmt >= totalAmt - 0.01;

      return {
        ...inv,
        __invId: invId,
        __totalAmt: totalAmt,
        __paid: paid,
        __due: due,
        __returnedAmt: returnedAmt,
        __fullyReturned: fullyReturned,
      };
    });

    const filtered = onlyOutstanding
      ? list.filter((x) => (Number(x.__due) || 0) > 0.009 && !x.__fullyReturned)
      : list;

    return filtered;
  }, [invoiceRows, onlyOutstanding, srAgg]);

  const invoiceSummary = useMemo(
    () =>
      rows.reduce(
        (acc, invoice) => {
          const totalReceivable =
            Number(invoice.__totalAmt ?? getInvoiceTotal(invoice)) || 0;
          const received =
            Number(invoice.__paid ?? getInvoicePaid(invoice)) || 0;
          const returned = Number(invoice.__returnedAmt || 0) || 0;

          acc.totalReceivable += totalReceivable;
          acc.received += received;
          acc.returned += returned;
          acc.netReceived += received - returned;
          acc.due +=
            Number(invoice.__due ?? getInvoiceOutstanding(invoice)) || 0;
          return acc;
        },
        {
          totalReceivable: 0,
          received: 0,
          returned: 0,
          netReceived: 0,
          due: 0,
        },
      ),
    [rows],
  );

  /* ✅ Customer transactions fetch */
  const shouldFetchTx = !!customer?.value;
  const {
    data: txRes,
    isLoading: txLoading,
    isFetching: txFetching,
    refetch: refetchTx,
  } = useGetCustomerTransactionsQuery(
    shouldFetchTx
      ? {
          page: 1,
          limit: 5000,
          customerId: customer.value,
          type: "ALL",
        }
      : skipToken,
  );

  const txRows = useMemo(() => txRes?.rows || txRes?.data || [], [txRes]);

  const getTxnInvoiceAllocations = useCallback((r) => [
    ...(Array.isArray(r?.allocations) ? r.allocations : []),
    ...(Array.isArray(r?.invoiceAllocations) ? r.invoiceAllocations : []),
  ], []);

  const hasTxnInvoiceAllocation = useCallback((r) =>
    getTxnInvoiceAllocations(r).some(
      (a) =>
        !!normId(a?.invoiceId) ||
        !!a?.invoiceNo ||
        !!a?.invNo ||
        !!a?.invoiceCode ||
        !!a?.invoiceRef,
    ), [getTxnInvoiceAllocations]);

  const hasTxnDirectInvoiceRef = useCallback((r) =>
    !!normId(r?.invoiceId) ||
    !!normId(r?.invoice?._id || r?.invoice) ||
    !!r?.invoiceNo ||
    !!r?.invNo ||
    !!r?.invoiceCode ||
    !!r?.invoiceRef, []);

  const isInvoiceReceiptTxn = useCallback((r) => {
    const hasPaymentAmount = Number(r?.credit || r?.invoicePaid || 0) > 0;
    const hasPaymentInfo = !!(
      r?.paymentMode ||
      r?.mode ||
      r?.paymentRefNo ||
      r?.refNo
    );

    return (
      (hasTxnInvoiceAllocation(r) || hasTxnDirectInvoiceRef(r)) &&
      (hasPaymentAmount || hasPaymentInfo)
    );
  }, [hasTxnDirectInvoiceRef, hasTxnInvoiceAllocation]);

  const isRawInvoiceTxn = useCallback((r) =>
    up(r?.sourceType || r?.type || r?.kind) === "INVOICE" &&
    !isInvoiceReceiptTxn(r), [isInvoiceReceiptTxn]);

  // ✅ identify invoice-related txns
  const isInvoiceRelatedTxn = useCallback((r) => {
    const t = up(r?.kind || r?.sourceType || r?.type);

    const isInvoiceLifecycleTxn =
      t.includes("INVOICE") ||
      t === "SALES_RETURN" ||
      t === "INVOICE_RETURN";

    return isInvoiceLifecycleTxn || hasTxnDirectInvoiceRef(r) || hasTxnInvoiceAllocation(r);
  }, [hasTxnDirectInvoiceRef, hasTxnInvoiceAllocation]);

  const pickInvoiceNoFromTxn = (r) => {
    const a =
      (Array.isArray(r?.allocations) && r.allocations[0]) ||
      (Array.isArray(r?.invoiceAllocations) && r.invoiceAllocations[0]) ||
      null;

    return (
      a?.invoiceNo ||
      a?.invoiceCode ||
      r?.invoiceNo ||
      r?.invNo ||
      r?.invoiceCode ||
      r?.sourceNo ||
      r?.invoiceRef ||
      r?.sourceRef ||
      "-"
    );
  };

  // ✅ Latest transactions on top
  const invoiceTxns = useMemo(() => {
    if (!customer?.value) return [];
    const raw = Array.isArray(txRows) ? txRows : [];

    // Only keep invoice-related transactions, but hide raw invoice creation entries.
    // Keep the ordering exactly as received from the API (no client-side sorting).
    return raw
      .filter(isInvoiceRelatedTxn)
      .filter((r) => !isRawInvoiceTxn(r));
  }, [customer?.value, isInvoiceRelatedTxn, isRawInvoiceTxn, txRows]);

  const filteredInvoiceTxns = useMemo(() => {
    const qq = up(txSearch).trim();
    if (!qq) return invoiceTxns;

    return invoiceTxns.filter((r) => {
      const invNo = pickInvoiceNoFromTxn(r);
      const ref =
        r?.paymentRefNo ||
        r?.refNo ||
        r?.sourceNo ||
        r?.sourceRef ||
        r?.invoiceRef ||
        "";

      const hay = up(
        `${txnTypeLabel(r)} ${invNo} ${ref} ${r?.note || ""} ${
          r?.paymentMode || r?.mode || ""
        }`,
      );
      return hay.includes(qq);
    });
  }, [invoiceTxns, txSearch]);

  const showTxSkeleton =
    !!customer?.value && txLoading && filteredInvoiceTxns.length === 0;

  /* Payment save */
  const [addReceipt, { isLoading: receiptLoading }] =
    useAddCustomerReceiptMutation();

  const handleReceiveClick = (inv) => {
    if (!inv) return;
    const due = Number(inv.__due ?? getInvoiceOutstanding(inv)) || 0;
    if (due <= 0.009) {
      toast.error("Invoice already cleared.");
      return;
    }
    setPayInvoice(inv);
    setPayOpen(true);
  };

  const handleOpenSalesReturn = (inv) => {
    if (!inv) return;

    const invId = inv.__invId || normId(inv?._id || inv?.invoiceId || inv);
    const invoiceTotal = Number(inv.__totalAmt ?? getInvoiceTotal(inv)) || 0;

    const returnedAmt = Number(srAgg.amountByInvoiceId?.[invId] || 0) || 0;
    const fullyReturned =
      invoiceTotal > 0 && returnedAmt >= invoiceTotal - 0.01;

    if (fullyReturned) {
      toast.error("This invoice is already fully returned.");
      return;
    }

    const outstandingTotal =
      Number(inv.__due ?? getInvoiceOutstanding(inv)) || 0;
    if (outstandingTotal > 0.009) {
      toast.error(
        `Invoice return allowed only for fully paid invoices. Pending Due: ₹ ${formatAmount(
          outstandingTotal,
        )}`,
      );
      return;
    }

    const paidTotal = Number(inv.__paid ?? getInvoicePaid(inv)) || 0;
    const returnedQtyByKey = srAgg.qtyByInvoiceIdByKey?.[invId] || {};

    setSalesReturnInvoice({
      ...inv,
      __invoiceTotal: invoiceTotal,
      __paidTotal: paidTotal,
      __outstandingTotal: outstandingTotal,
      __alreadyReturnedAmount: returnedAmt,
      __returnedQtyByKey: returnedQtyByKey,
    });
    setSalesReturnOpen(true);
  };

  const handleSubmitPayment = async (payload) => {
    try {
      const customerId =
        customer?.value ||
        payload?.customerId ||
        payInvoice?.customerId?._id ||
        payInvoice?.customerId ||
        "";

      if (!customerId) {
        toast.error("Please select customer.");
        return;
      }

      const saved = await addReceipt({
        customerId,
        mode: payload.mode,
        amount: payload.amount,
        refNo: payload.refNo,
        note: payload.note,
        kind: "INVOICE",
        allocations: payload.allocations,
      }).unwrap();

      setPayOpen(false);
      setPayInvoice(null);

      toast.success("Receipt saved successfully");

      await Promise.allSettled(
        [refetchInvoices, refetchTx]?.filter(Boolean).map((fn) => fn()),
      );

      const data = saved?.data ?? saved;
      const ledgerId =
        data?.ledgerId || data?.data?.ledgerId || data?._id || data?.id || null;

      if (ledgerId) {
        setPrintConfirm({
          open: true,
          row: { _id: ledgerId, printUrl: data?.printUrl },
        });
      }
    } catch (e) {
      const original = Number(e?.originalStatus || 0);
      const is2xx = original >= 200 && original < 300;
      const isParsingOk = e?.status === "PARSING_ERROR" && is2xx;

      if (isParsingOk) {
        setPayOpen(false);
        setPayInvoice(null);
        toast.success("Receipt saved successfully");

        await Promise.allSettled(
          [refetchInvoices, refetchTx]?.filter(Boolean).map((fn) => fn()),
        );
        return;
      }

      // eslint-disable-next-line no-console
      console.error("invoice receipt error", e);
      toast.error(e?.data?.message || e?.error || "Failed to save receipt");
    }
  };

  useEffect(() => {
    // customer change -> default mobile view to invoices
    setMobileView("INVOICES");
  }, [customer?.value]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minWidth: 0,
      minHeight: 38,
      height: 38,
      borderRadius: 16,
      borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 3px #7c3aed22" : "none",
      "&:hover": { borderColor: "#7c3aed" },
      fontSize: "0.85rem",
      backgroundColor: "white",
    }),
    valueContainer: (base) => ({
      ...base,
      minWidth: 0,
      overflow: "hidden",
      padding: "0 8px",
      flexWrap: "nowrap",
    }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#0f172a",
      fontWeight: 700,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.75rem",
      fontWeight: 600,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
    indicatorsContainer: (base) => ({ ...base, height: 38 }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.85rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      backgroundColor: state.isFocused
        ? "#ede9fe"
        : state.isSelected
          ? "#ddd6fe"
          : "white",
      color: "#0f172a",
      fontWeight: state.isSelected ? 700 : 600,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 40,
      borderRadius: 14,
      overflow: "hidden",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const paginationSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 8,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  const invCountLabel =
    customer?.value && !invLoading ? `(${rows.length})` : "";

  const txCountLabel = customer?.value ? `(${filteredInvoiceTxns.length})` : "";

  return (
    <div className="h-[calc(100vh-140px)] w-full md:h-[calc(100vh-70px)]">
      <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-lg">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2 text-white shadow-sm sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
              <FaMoneyBillWave className="text-sm" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-[15px] font-semibold tracking-tight sm:text-lg">
                  Invoice Payments
                </h2>
                {(invFetching || txFetching) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px]">
                    <Spinner size={10} /> refreshing…
                  </span>
                )}
              </div>
              <p className="hidden truncate text-[11px] text-violet-50 lg:block">
                Receive payments and review invoice history
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen((p) => !p)}
              className="hidden h-9 items-center gap-2 rounded-xl bg-white/15 px-3 text-[11px] font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:inline-flex"
              title={onlyOutstanding ? "Filters · Due invoices" : "Filters · All invoices"}
            >
              <FaFilter className="text-xs" />
              Filters
              {filtersOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>

            <button
              onClick={() => {
                refetchInvoices?.();
                refetchTx?.();
              }}
              disabled={invFetching || txFetching}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 disabled:opacity-60 sm:rounded-full"
            >
              <FaRedoAlt className="text-xs" />
              Refresh
            </button>
          </div>
        </div>

        {/* Top bar (customer + quick actions) */}
        <div className="shrink-0 border-b bg-white px-3 py-2 sm:px-4">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex-1 min-w-0">
              <Select
                isClearable
                isLoading={customersLoading}
                options={customerOptions}
                value={customer}
                onChange={(val) => {
                  setCustomer(val || null);
                  setPage(1);
                  setTxSearch("");
                }}
                placeholder="Customer…"
                styles={selectStyles}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </div>

            <div className="shrink-0 lg:hidden">
              <SegTabs
                value={mobileView}
                onChange={setMobileView}
                tabs={[
                  { value: "INVOICES", label: "Invoices", icon: <FaList /> },
                  {
                    value: "TXNS",
                    label: "History",
                    icon: <FaMoneyBillWave />,
                  },
                ]}
              />
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen((p) => !p)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 sm:hidden"
              title={onlyOutstanding ? "Filters · Due invoices" : "Filters · All invoices"}
              aria-label="Open invoice filters"
            >
              {filtersOpen ? (
                <FiChevronUp className="text-[16px]" />
              ) : (
                <FaFilter className="text-[14px]" />
              )}
            </button>
          </div>

          <div className="mt-1.5 flex snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth text-[10px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:snap-none sm:grid-cols-5 sm:overflow-visible">
            <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-violet-100 bg-violet-50/70 px-2 text-violet-700 sm:min-w-0">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
              <span className="shrink-0 font-medium">Receivable</span>
              <span className="truncate font-bold tabular-nums">₹ {formatAmount(invoiceSummary.totalReceivable)}</span>
            </div>
            <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/70 px-2 text-emerald-700 sm:min-w-0">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
              <span className="shrink-0 font-medium">Received</span>
              <span className="truncate font-bold tabular-nums">₹ {formatAmount(invoiceSummary.received)}</span>
            </div>
            <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-rose-100 bg-rose-50/70 px-2 text-rose-700 sm:min-w-0">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
              <span className="shrink-0 font-medium">Returned</span>
              <span className="truncate font-bold tabular-nums">₹ {formatAmount(invoiceSummary.returned)}</span>
            </div>
            <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 text-slate-700 sm:min-w-0">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
              <span className="shrink-0 font-medium">Net</span>
              <span className="truncate font-bold tabular-nums">₹ {formatAmount(invoiceSummary.netReceived)}</span>
            </div>
            <div className="inline-flex h-8 min-w-[48%] snap-start items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50/70 px-2 text-amber-700 sm:min-w-0">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              <span className="shrink-0 font-medium">Due</span>
              <span className="truncate font-bold tabular-nums">₹ {formatAmount(invoiceSummary.due)}</span>
            </div>
          </div>

          {/* Filters modal */}
          <AnimatePresence initial={false}>
            {filtersOpen && (
              <motion.div
                className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setFiltersOpen(false)}
              >
                <motion.div
                  className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
                  initial={{ scale: 0.94, y: 24, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  exit={{ scale: 0.94, y: 24, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex items-center justify-between bg-[#9481ce] px-4 py-3 text-white">
                    <div>
                      <h3 className="text-sm font-semibold">Invoice filters</h3>
                      <p className="mt-0.5 text-[10px] text-violet-100">
                        Refine invoices by date range and reference.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFiltersOpen(false)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                      aria-label="Close filters"
                    >
                      <FaTimesCircle />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="text-[11px] font-semibold text-slate-700">
                        Invoice status
                      </label>
                      <div className="mt-1 grid grid-cols-2 rounded-xl bg-slate-100 p-1">
                        <button
                          type="button"
                          onClick={() => {
                            setOnlyOutstanding(true);
                            setPage(1);
                          }}
                          className={`rounded-lg px-3 py-2 text-[11px] font-semibold transition ${
                            onlyOutstanding
                              ? "bg-white text-slate-900 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          Due invoices
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOnlyOutstanding(false);
                            setPage(1);
                          }}
                          className={`rounded-lg px-3 py-2 text-[11px] font-semibold transition ${
                            !onlyOutstanding
                              ? "bg-white text-slate-900 shadow-sm"
                              : "text-slate-500 hover:text-slate-700"
                          }`}
                        >
                          All invoices
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold text-slate-700">
                        From
                      </label>
                      <input
                        type="date"
                        value={from}
                        onChange={(e) => {
                          setFrom(e.target.value);
                          setPage(1);
                        }}
                        className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold text-slate-700">
                        To
                      </label>
                      <input
                        type="date"
                        value={to}
                        onChange={(e) => {
                          setTo(e.target.value);
                          setPage(1);
                        }}
                        className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-[11px] font-semibold text-slate-700">
                        Search
                      </label>
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                          setQ(e.target.value);
                          setPage(1);
                        }}
                        placeholder="Invoice no, code, branch…"
                        className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => {
                        setFrom("");
                        setTo("");
                        setQ("");
                        setPage(1);
                        setOnlyOutstanding(false);
                      }}
                      className="px-2 py-2 text-[11px] font-medium text-slate-600 hover:text-slate-900"
                    >
                      Reset filters
                    </button>

                    <button
                      type="button"
                      onClick={() => setFiltersOpen(false)}
                      className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                    >
                      Done
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-hidden bg-white">
          {/* Desktop: 2 columns; Mobile: tabs */}
          <div className="grid h-full grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px]">
            {/* LEFT: invoices */}
            <div
              className={`min-w-0 h-full overflow-hidden ${
                mobileView === "TXNS" ? "hidden lg:block" : ""
              }`}
            >
              <div className="h-full flex flex-col">
                <div className="border-b bg-white px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        Invoices{" "}
                        <span className="text-[11px] text-slate-500 font-semibold">
                          {invCountLabel}
                        </span>
                      </div>
                      <div className="hidden text-[11px] text-slate-500 sm:block">
                        {onlyOutstanding
                          ? "Choose an invoice below to receive its outstanding payment"
                          : "View paid invoices or create a sales return"}
                      </div>
                    </div>

                    <div className="text-[11px] text-slate-500">
                      {invFetching ? (
                        <span className="inline-flex items-center gap-2">
                          <Spinner size={14} /> loading…
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-auto">
                    {/* Desktop table */}
                    <div className="hidden md:block">
                      <div className="min-w-[700px]">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0 z-10 border-b">
                            <tr className="text-[9px] text-slate-600 uppercase tracking-wide">
                              <th className="px-2 py-2 text-left font-semibold">
                                Date
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Invoice
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Branch
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Amount
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Paid
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Due
                              </th>
                              <th className="px-2 py-2 text-left font-semibold">
                                Returned
                              </th>
                              <th className="px-2 py-2 text-right font-semibold">
                                Actions
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y">
                            {invLoading ? (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="px-4 py-10 text-center text-slate-500"
                                >
                                  <div className="inline-flex items-center gap-2">
                                    <Spinner size={18} /> Loading invoices…
                                  </div>
                                </td>
                              </tr>
                            ) : rows.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="px-4 py-10 text-center text-slate-500"
                                >
                                  No invoices found.
                                </td>
                              </tr>
                            ) : (
                              rows.map((inv) => {
                                const due = Number(inv.__due) || 0;
                                const returnedAmt =
                                  Number(inv.__returnedAmt) || 0;
                                const fullyPaid = due <= 0.009;
                                const fullyReturned = !!inv.__fullyReturned;

                                const invNo =
                                  inv.sourceNo ||
                                  inv.invNo ||
                                  inv.invoiceNo ||
                                  inv.invCode ||
                                  "-";

                                return (
                                  <tr
                                    key={inv.__invId || inv._id}
                                    className="hover:bg-violet-50/35 transition"
                                  >
                                    <td className="px-2 py-2 align-top text-slate-700">
                                      <div className="text-[9px] font-semibold">
                                        {formatDateTime(
                                          inv.invDate ||
                                            inv.date ||
                                            inv.createdAt,
                                        )}
                                      </div>
                                    </td>

                                    <td className="px-2 py-2 align-top text-slate-900">
                                      <div className="flex flex-col">
                                        <span className="text-[9px] font-extrabold">
                                          {invNo}
                                        </span>
                                        {fullyReturned ? (
                                          <span className="mt-1 inline-flex w-fit text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-semibold">
                                            FULLY RETURNED
                                          </span>
                                        ) : null}
                                      </div>
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-slate-600">
                                      {inv.branchId?.branchName ||
                                        inv.branchName ||
                                        "-"}
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-left">
                                      <MoneyPill value={inv.__totalAmt} />
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-left">
                                      <MoneyPill value={inv.__paid} tone="good" />
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-left">
                                      <MoneyPill value={due} tone={due > 0 ? "bad" : "good"} />
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-left">
                                      <MoneyPill value={returnedAmt} />
                                    </td>

                                    <td className="px-2 py-2 text-[9px] align-top text-right">
                                      <div className="inline-flex items-center gap-2 justify-end">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleReceiveClick(inv)
                                          }
                                          disabled={
                                            !customer ||
                                            due <= 0.009 ||
                                            fullyReturned
                                          }
                                          className="px-2 py-1 rounded-full border border-emerald-200 text-[9px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50"
                                        >
                                          Receive Payment
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleOpenSalesReturn(inv)
                                          }
                                          disabled={
                                            !customer ||
                                            !fullyPaid ||
                                            fullyReturned
                                          }
                                          className="px-2 py-1 rounded-full border border-amber-200 text-[9px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                                        >
                                          Invoice Return
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="space-y-2 bg-slate-50 p-2.5 md:hidden">
                      {invLoading ? (
                        <div className="rounded-2xl border bg-white p-4 text-center text-slate-500">
                          <Spinner size={18} /> Loading invoices…
                        </div>
                      ) : rows.length === 0 ? (
                        <div className="rounded-2xl border bg-white p-4 text-center text-slate-500">
                          No invoices found.
                        </div>
                      ) : (
                        rows.map((inv) => {
                          const due = Number(inv.__due) || 0;
                          const returnedAmt = Number(inv.__returnedAmt) || 0;
                          const fullyPaid = due <= 0.009;
                          const fullyReturned = !!inv.__fullyReturned;

                          const invNo =
                            inv.sourceNo ||
                            inv.invNo ||
                            inv.invoiceNo ||
                            inv.invCode ||
                            "-";

                          return (
                            <div
                              key={inv.__invId || inv._id}
                              className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                            >
                              <div className="p-3">
                                <div className="flex min-w-0 items-start justify-between gap-2">
                                  <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <div className="truncate text-[13px] font-semibold text-slate-900">
                                      {invNo}
                                    </div>
                                    {fullyReturned ? (
                                      <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 font-semibold">
                                        RETURNED
                                      </span>
                                    ) : null}
                                  </div>

                                  <div className="mt-1 text-[11px] text-slate-500">
                                    {formatDateTime(
                                      inv.invDate || inv.date || inv.createdAt,
                                    )}
                                    {inv.branchId?.branchName ? (
                                      <span> · {inv.branchId.branchName}</span>
                                    ) : null}
                                  </div>
                                  </div>
                                  <div className="shrink-0 text-right">
                                    <div className="text-[9px] uppercase tracking-wide text-slate-400">Due</div>
                                    <div className={`text-sm font-bold tabular-nums ${due > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                      ₹ {formatAmount(due)}
                                    </div>
                                  </div>
                                </div>

                                  <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-2.5">
                                    <MoneyPill label="Total" value={inv.__totalAmt} />
                                    <MoneyPill label="Paid" value={inv.__paid} tone="good" />
                                    {returnedAmt > 0 ? (
                                      <MoneyPill label="Returned" value={returnedAmt} />
                                    ) : null}
                                  </div>

                                <div className="mt-3 grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleReceiveClick(inv)}
                                    disabled={
                                      !customer || due <= 0.009 || fullyReturned
                                    }
                                    className="rounded-lg bg-emerald-600 px-3 py-2 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:bg-slate-100 disabled:text-slate-400"
                                  >
                                    Receive Payment
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleOpenSalesReturn(inv)}
                                    disabled={
                                      !customer || !fullyPaid || fullyReturned
                                    }
                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:bg-slate-50 disabled:text-slate-300"
                                  >
                                    Create Return
                                  </button>
                                </div>
                              </div>

                              {fullyReturned ? (
                                <div className="border-t bg-amber-50/40 px-3 py-2 text-[11px] text-amber-800">
                                  This invoice is fully returned.
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: transactions */}
            <motion.div
              variants={fadeUp(0.1)}
              initial="hidden"
              animate="visible"
              className={`min-w-0 h-full overflow-hidden border-t bg-white lg:border-l lg:border-t-0 ${
                mobileView === "INVOICES" ? "hidden lg:block" : ""
              }`}
            >
              <div className="h-full flex flex-col">
                <div className="border-b bg-white px-3 py-2 sm:px-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        Activity{" "}
                        <span className="text-[11px] text-slate-500 font-semibold">
                          {txCountLabel}
                        </span>
                      </div>
                      {/* <div className="text-[11px] text-slate-500 mt-0.5">
                        {customer?.value
                          ? "Selected customer ke all invoices ke txns"
                          : "Customer select karo, invoice txns yaha dikhेंगे."}
                      </div> */}
                    </div>

                    <div className="flex items-center gap-2">
                      <ListLoadingPill show={!!customer?.value && txLoading} />
                    </div>
                  </div>

                  <div className="mt-1.5">
                    <input
                      value={txSearch}
                      onChange={(e) => setTxSearch(e.target.value)}
                      placeholder="Search activity…"
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[10px] outline-none transition focus:border-violet-300 focus:ring-1 focus:ring-violet-100 disabled:bg-slate-50"
                      disabled={!customer?.value}
                    />
                  </div>

                  {/* <div className="mt-2">
                    <input
                      value={txSearch}
                      onChange={(e) => setTxSearch(e.target.value)}
                      placeholder="Search invoice no / ref / note…"
                      className="w-full px-3 py-2 rounded-2xl text-[11px] border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400"
                      disabled={!customer?.value}
                    />
                  </div> */}
                </div>

                <div className="relative flex-1 overflow-hidden p-2">
                  <ListShimmerOverlay show={showTxSkeleton} />

                  {!customer?.value ? (
                    <EmptyState
                      icon={<FaList className="text-[22px]" />}
                      title="Select customer"
                      subtitle="Choose a customer above to see payment and return history."
                    />
                  ) : showTxSkeleton ? (
                    <div className="space-y-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonTxRow key={i} />
                      ))}
                    </div>
                  ) : filteredInvoiceTxns.length === 0 ? (
                    <EmptyState
                      icon={<FaMoneyBillWave className="text-[22px]" />}
                      title="No payment history"
                      subtitle="No payments or returns match the current selection."
                    />
                  ) : (
                    <div className="h-full space-y-2 overflow-auto pr-1">
                      {filteredInvoiceTxns.map((r, idx) => {
                        const invNo = pickInvoiceNoFromTxn(r);
                        const amt = pickTxnAmount(r);
                        const tone = pickTxnTone(r);

                        const mode = r?.paymentMode || r?.mode || "-";
                        const ref =
                          r?.paymentRefNo ||
                          r?.refNo ||
                          r?.sourceRef ||
                          r?.sourceNo ||
                          "";

                        const canPrint = !!(
                          r?._id ||
                          r?.ledgerId ||
                          r?.printUrl
                        );

                        return (
                          <motion.div
                            key={r?._id || `${idx}-${invNo}-${ref}`}
                            custom={Math.min(idx, 10)}
                            variants={listVariants}
                            initial="hidden"
                            animate="visible"
                            className="rounded-xl border border-slate-200 bg-white px-2.5 py-2.5 transition-colors hover:border-violet-200"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold border bg-slate-50 text-slate-700 border-slate-200">
                                    {txnTypeLabel(r)}
                                  </span>

                                  <div className="text-[12px] font-extrabold text-slate-900 truncate">
                                    Invoice: {invNo}
                                  </div>
                                </div>

                                <div className="text-[10px] text-slate-500 mt-1">
                                  {formatDateTime(r?.date || r?.createdAt)}
                                  {" · "}
                                  {mode}
                                  {ref ? ` · Ref: ${ref}` : ""}
                                </div>

                                {r?.note ? (
                                  <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                                    {r.note}
                                  </div>
                                ) : null}
                              </div>

                              <div className="text-right shrink-0">
                                <div className="mb-1">
                                  <MoneyPill value={amt} tone={tone} />
                                </div>

                                <button
                                  type="button"
                                  disabled={!canPrint}
                                  onClick={() => handleReceiptPrint(r)}
                                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                                    canPrint
                                      ? "border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50"
                                      : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                  }`}
                                  title={
                                    canPrint
                                      ? "Print slip"
                                      : "Slip not available"
                                  }
                                >
                                  <FiPrinter className="text-[12px]" />
                                  Slip
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-slate-50/80 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
          <div className="hidden text-slate-600 sm:block">
            Page <span className="font-semibold">{page}</span> of{" "}
            <span className="font-semibold">{totalPages}</span> •{" "}
            <span className="font-semibold">{total ?? 0}</span> invoice(s)
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
            <div className="flex shrink-0 items-center gap-1 text-slate-600">
              <span className="hidden text-xs sm:inline">Rows / page</span>
              <Select
                value={{ value: limit, label: String(limit) }}
                options={[10, 25, 50, 100].map((number) => ({ value: number, label: String(number) }))}
                onChange={(option) => {
                  setLimit(Number(option?.value) || 25);
                  setPage(1);
                }}
                isDisabled={invFetching || invLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[54px]"
                aria-label="Rows per page"
              />
              <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || invFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <span className="sm:hidden">&lsaquo;</span><span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{ value: page, label: String(page) }}
                options={Array.from({ length: totalPages }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={invFetching || invLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page"
              />
              <span>/ {totalPages}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base shadow-sm disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= totalPages || invFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <span className="sm:hidden">&rsaquo;</span><span className="hidden sm:inline">Next</span>
            </button>
            </div>
          </div>
        </div>

        {/* MODALS */}
        <InvoiceReceivePaymentModal
          open={payOpen}
          onClose={() => {
            if (receiptLoading) return;
            setPayOpen(false);
            setPayInvoice(null);
          }}
          invoice={payInvoice}
          customerLabel={customer?.label}
          loading={receiptLoading}
          onSubmit={handleSubmitPayment}
        />

        {salesReturnOpen && (
          <CreateSalesReturnModal
            open={salesReturnOpen}
            onClose={() => {
              setSalesReturnOpen(false);
              setSalesReturnInvoice(null);
            }}
            invoice={salesReturnInvoice}
            onSuccess={async () => {
              setSalesReturnOpen(false);
              setSalesReturnInvoice(null);
              toast.success("Invoice return created");
              await Promise.allSettled(
                [refetchInvoices, refetchTx]?.filter(Boolean).map((fn) => fn()),
              );
            }}
          />
        )}

        {printConfirm?.open && (
          <ConfirmDialog
            open={printConfirm?.open}
            title="Print Receipt?"
            message="Receipt saved successfully. Do you want to print the receipt now?"
            confirmText="Yes, Print"
            cancelText="No"
            onCancel={() => {
              setPrintConfirm({ open: false, row: null });
            }}
           onConfirm={() => {
  const row = printConfirm.row;
  setPrintConfirm({ open: false, row: null });

  if (row?._id) handleReceiptPrint(row);
}}
          />
        )}

        <InvoicePrintPreviewModal
  open={receiptPreview.open}
  html={receiptPreview.html}
  loading={receiptPreview.loading}
  onClose={() =>
    setReceiptPreview({
      open: false,
      html: "",
      loading: false,
    })
  }
/>
      </div>
    </div>
  );
};

export default InvoicePayments;
